import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import { Role } from '../../types/auth';
import { requestFieldErrors, requestMessage } from '../../utils/requestError';
import { useAuth } from '../../contexts/AuthContext';
import {
  SSOProvider,
  SSOProviderInput,
  SSOProviderKind,
  SSOSettings,
  SSOTestResult,
  useDeleteSSOSetting,
  useSSOSetting,
  useTestSSO,
  useUpdateSSOSetting,
} from '../../api/sso';
import { useTenantProfile, useUpdateTenantProfile } from '../../api/tenantProfile';
import AdminPageHeader from './AdminPageHeader';

/**
 * Admin → Sign-in: how this organisation's people sign in, and its sign-in link.
 *
 * Two single sign-on modes: an OpenID Connect app the organisation registers
 * in its own IdP (Entra ID, Okta…), or the shared Google client where the
 * organisation only lists its Workspace domain. The client secret is
 * write-only — the API reports that one is stored, never its value.
 */

/** Joins "a.gov.au", "b.gov.au" and "c.gov.au" the way a sentence would. */
const listDomains = (domains: string[]): string => {
  if (domains.length <= 1) return domains[0] ?? '';
  return `${domains.slice(0, -1).join(', ')} and ${domains[domains.length - 1]}`;
};

/** The one-line summary at the top: what is on now, and what that means for people. */
const describeCurrentMethod = (
  setting: SSOSettings,
  provider: SSOProvider | null
): { title: string; sentence: string } => {
  if (!setting.configured || !provider) {
    return {
      title: 'Password only',
      sentence: 'Everyone signs in with their username and password.',
    };
  }
  const isGoogle = provider.provider_kind === 'google_shared';
  const title = isGoogle
    ? 'Google Workspace'
    : `${provider.display_name || 'Your identity provider'} (your identity provider)`;
  if (!provider.enabled) {
    return {
      title,
      sentence: 'Single sign-on is switched off, so everyone signs in with a password for now.',
    };
  }
  const who = provider.allowed_email_domains.length
    ? `People in ${listDomains(provider.allowed_email_domains)}`
    : 'Your people';
  const button = isGoogle
    ? '“Sign in with Google”'
    : `“Continue with ${provider.display_name || 'your organisation'}”`;
  const passwords = provider.enforce_sso ? 'Single sign-on is required.' : 'Passwords still work.';
  return { title, sentence: `${who} click ${button}. ${passwords}` };
};

interface FormState {
  provider_kind: SSOProviderKind;
  display_name: string;
  enabled: boolean;
  discovery_url: string;
  client_id: string;
  client_secret: string;
  allowed_email_domains: string[];
  jit_enabled: boolean;
  jit_default_role_id: number | null;
  enforce_sso: boolean;
  sso_session_hours: number;
}

const EMPTY: FormState = {
  provider_kind: 'oidc',
  display_name: '',
  enabled: true,
  discovery_url: '',
  client_id: '',
  client_secret: '',
  allowed_email_domains: [],
  jit_enabled: false,
  jit_default_role_id: null,
  enforce_sso: false,
  sso_session_hours: 12,
};


export default function SSOManagement({ roles }: { roles: Role[] }) {
  const { user } = useAuth();
  const { data: setting, isLoading } = useSSOSetting();
  const update = useUpdateSSOSetting();
  const remove = useDeleteSSOSetting();
  const test = useTestSSO();
  const { data: profile } = useTenantProfile();
  const updateProfile = useUpdateTenantProfile();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [testResult, setTestResult] = useState<SSOTestResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [confirmEnforce, setConfirmEnforce] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [snackbar, setSnackbar] = useState<string | null>(null);
  const [slugDraft, setSlugDraft] = useState('');
  const [slugError, setSlugError] = useState<string | null>(null);

  const provider = setting?.provider ?? null;

  useEffect(() => {
    if (profile) {
      setSlugDraft(profile.slug);
      setSlugError(null);
    }
  }, [profile]);

  const serverForm: FormState = useMemo(
    () =>
      provider
        ? {
            provider_kind: provider.provider_kind,
            display_name: provider.display_name,
            enabled: provider.enabled,
            discovery_url: provider.discovery_url,
            client_id: provider.client_id,
            client_secret: '',
            allowed_email_domains: provider.allowed_email_domains,
            jit_enabled: provider.jit_enabled,
            jit_default_role_id: provider.jit_default_role_id,
            enforce_sso: provider.enforce_sso,
            sso_session_hours: provider.sso_session_hours,
          }
        : EMPTY,
    [provider]
  );

  useEffect(() => {
    setForm(serverForm);
    setErrors({});
  }, [serverForm]);

  if (isLoading || !setting) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const isGoogle = form.provider_kind === 'google_shared';
  const dirty = JSON.stringify(form) !== JSON.stringify(serverForm);
  // Turning enforcement on needs proof SSO works for the person doing it:
  // the server accepts it only from a session that came through SSO.
  const canTurnOnEnforce = !!user?.session_is_sso || !!user?.is_superuser || !!provider?.enforce_sso;

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = () => {
    setErrors({});
    const body: SSOProviderInput = {
      ...form,
      client_secret: form.client_secret ? form.client_secret : null,
      allowed_email_domains: form.allowed_email_domains,
    };
    update.mutate(body, {
      onSuccess: () => {
        setSnackbar(
          form.enabled
            ? 'Single sign-on saved. Test it by signing in from a private window.'
            : 'Single sign-on saved (switched off).'
        );
      },
      onError: (err: unknown) => {
        const fieldErrors = requestFieldErrors(err);
        setErrors(fieldErrors);
        if (!Object.keys(fieldErrors).length || fieldErrors.code) {
          setSnackbar(requestMessage(err, 'Could not save single sign-on'));
        }
      },
    });
  };

  const handleTest = () => {
    setTestResult(null);
    setTestError(null);
    test.mutate(
      { provider_kind: form.provider_kind, discovery_url: form.discovery_url || undefined },
      {
        onSuccess: (result) => setTestResult(result),
        onError: (err: unknown) => setTestError(requestMessage(err, 'The provider could not be reached')),
      }
    );
  };

  const handleRemove = () => {
    remove.mutate(undefined, {
      onSuccess: (result) => {
        setConfirmRemove(false);
        setTestResult(null);
        setSnackbar(
          `Single sign-on removed. ${result.identities_removed} linked account${
            result.identities_removed === 1 ? '' : 's'
          } now sign in with a password.`
        );
      },
      onError: (err: unknown) => setSnackbar(requestMessage(err, 'Could not remove single sign-on')),
    });
  };

  const copyRedirect = () => {
    navigator.clipboard?.writeText(setting.redirect_uri).then(
      () => setSnackbar('Redirect URI copied'),
      () => setSnackbar('Copy failed — select the text instead')
    );
  };

  const loginLink = profile ? `${window.location.origin}${profile.login_path}` : '';
  const slugDirty = !!profile && slugDraft.trim() !== profile.slug;
  const canEditSlug = !!profile?.can_edit;

  const copyLoginLink = () => {
    navigator.clipboard?.writeText(loginLink).then(
      () => setSnackbar('Sign-in link copied'),
      () => setSnackbar('Copy failed — select the text instead')
    );
  };

  const handleSaveSlug = () => {
    setSlugError(null);
    updateProfile.mutate(
      { slug: slugDraft.trim() },
      {
        onSuccess: () => setSnackbar('Sign-in link updated. Share the new link with your people.'),
        onError: (err: unknown) => {
          const fieldErrors = requestFieldErrors(err);
          if (fieldErrors.slug) setSlugError(fieldErrors.slug);
          else setSnackbar(requestMessage(err, 'Could not update the sign-in link'));
        },
      }
    );
  };

  const current = describeCurrentMethod(setting, provider);
  const ssoOn = !!provider?.enabled;

  return (
    <Box sx={{ textAlign: 'left' }}>
      <AdminPageHeader
        title="Sign-in"
        description="How your people sign in to Mitigate, and your organisation's sign-in link."
        audience="admins"
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 400px' },
          gap: 3,
          alignItems: 'start',
        }}
      >
      {/* Left column: the method and its settings. */}
      <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Let your people sign in to Mitigate with your organisation's own
        accounts. Multi-factor authentication and other sign-in rules are then
        applied by your identity provider.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          How do your people sign in?
        </Typography>
        <RadioGroup
          value={form.provider_kind}
          onChange={(e) => set('provider_kind', e.target.value as SSOProviderKind)}
        >
          <FormControlLabel
            value="oidc"
            control={<Radio />}
            label="Your identity provider (Microsoft Entra ID, Okta, other)"
          />
          <FormControlLabel
            value="google_shared"
            disabled={!setting.shared_google_available}
            control={<Radio />}
            label={
              setting.shared_google_available
                ? 'Google Workspace'
                : 'Google Workspace — not available in this environment yet'
            }
          />
        </RadioGroup>
        {errors.provider_kind && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {errors.provider_kind}
          </Alert>
        )}
      </Paper>

      {!isGoogle && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            1. Register Mitigate in your identity provider
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Create a web application and give it this redirect URI. In Entra ID
            this is App registrations → New registration → platform Web.
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Redirect URI"
            value={setting.redirect_uri}
            slotProps={{
              input: {
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Copy">
                      <IconButton size="small" onClick={copyRedirect}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              },
            }}
          />

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 0.5 }}>
            2. Enter the details it gives you
          </Typography>
          <TextField
            fullWidth
            size="small"
            margin="dense"
            label="Discovery URL"
            placeholder="https://login.microsoftonline.com/<Directory ID>/v2.0/.well-known/openid-configuration"
            value={form.discovery_url}
            onChange={(e) => set('discovery_url', e.target.value)}
            error={!!errors.discovery_url}
            helperText={
              errors.discovery_url ||
              "Your provider's OpenID configuration address. For Entra ID, use your Directory (tenant) ID — not /common/."
            }
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 2 }}>
            <TextField
              fullWidth
              size="small"
              margin="dense"
              label="Application (client) ID"
              value={form.client_id}
              onChange={(e) => set('client_id', e.target.value)}
              error={!!errors.client_id}
              helperText={errors.client_id || ' '}
            />
            <TextField
              fullWidth
              size="small"
              margin="dense"
              type="password"
              autoComplete="new-password"
              label="Client secret"
              placeholder={
                provider?.has_client_secret
                  ? `Saved, ending ${provider.client_secret_hint || '••••'} — leave blank to keep it`
                  : ''
              }
              value={form.client_secret}
              onChange={(e) => set('client_secret', e.target.value)}
              error={!!errors.client_secret}
              helperText={
                errors.client_secret ||
                'Copy the secret’s value, not its ID. Note when it expires: sign-in stops when it does.'
              }
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleTest}
              disabled={test.isPending || !form.discovery_url}
            >
              {test.isPending ? 'Checking…' : 'Check connection'}
            </Button>
            <Typography variant="caption" color="text.secondary">
              Checks your provider can be reached. The real test is signing in
              from a private window once saved.
            </Typography>
          </Box>
          {testError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {testError}
            </Alert>
          )}
          {testResult && (
            <Alert severity={testResult.warnings.length ? 'warning' : 'success'} sx={{ mt: 1 }}>
              Reached {testResult.issuer} ({testResult.jwks_keys} signing key
              {testResult.jwks_keys === 1 ? '' : 's'}).
              {testResult.warnings.map((w) => (
                <Box key={w} component="div">
                  {w}
                </Box>
              ))}
            </Alert>
          )}
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          {isGoogle ? 'Your Google Workspace' : '3. Who signs in this way'}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 2 }}>
        <TextField
          fullWidth
          size="small"
          margin="dense"
          label="Button name"
          placeholder="e.g. Acme Council"
          value={form.display_name}
          onChange={(e) => set('display_name', e.target.value)}
          error={!!errors.display_name}
          helperText={errors.display_name || `Shown on the sign-in page as “Continue with …”.`}
        />
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={form.allowed_email_domains}
          onChange={(_e, value) =>
            set(
              'allowed_email_domains',
              value.map((v) => String(v).trim().toLowerCase().replace(/^@/, '')).filter(Boolean)
            )
          }
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip size="small" label={option} {...getTagProps({ index })} key={option} />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              margin="dense"
              label="Email domains"
              placeholder="acme.com.au — press Enter after each"
              error={!!errors.allowed_email_domains}
              helperText={
                errors.allowed_email_domains ||
                'People whose email ends in one of these are sent to single sign-on.'
              }
            />
          )}
        />
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <FormControlLabel
          control={<Switch checked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} />}
          label="Single sign-on is on"
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6, mb: 2 }}>
          While off, everyone signs in with a password as before.
        </Typography>

        <FormControlLabel
          control={
            <Switch checked={form.jit_enabled} onChange={(e) => set('jit_enabled', e.target.checked)} />
          }
          label="Create accounts automatically"
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6, mb: 1 }}>
          Anyone your identity provider lets in, with an email in your domains,
          gets a Mitigate account on their first sign-in. Leave off to add
          people yourself in Users.
        </Typography>
        {form.jit_enabled && (
          <FormControl size="small" sx={{ ml: 6, mb: 2, minWidth: 260 }}>
            <InputLabel id="sso-default-role">Group for new accounts</InputLabel>
            <Select
              labelId="sso-default-role"
              label="Group for new accounts"
              value={form.jit_default_role_id ?? ''}
              onChange={(e) =>
                set('jit_default_role_id', e.target.value === '' ? null : Number(e.target.value))
              }
              error={!!errors.jit_default_role_id}
            >
              <MenuItem value="">
                <em>No group</em>
              </MenuItem>
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Box>
          <Tooltip
            title={canTurnOnEnforce ? '' : 'Sign in with single sign-on yourself first, then turn this on.'}
          >
            <span>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.enforce_sso}
                    disabled={!form.enforce_sso && !canTurnOnEnforce}
                    onChange={(e) => {
                      if (e.target.checked) setConfirmEnforce(true);
                      else set('enforce_sso', false);
                    }}
                  />
                }
                label="Require single sign-on"
              />
            </span>
          </Tooltip>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6, mb: 2 }}>
            Password sign-in stops working for everyone in your organisation.
            FiSci support can still sign in to help you.
          </Typography>
        </Box>

        <TextField
          size="small"
          type="number"
          label="Sign in again after (hours)"
          value={form.sso_session_hours}
          onChange={(e) => set('sso_session_hours', Number(e.target.value))}
          error={!!errors.sso_session_hours}
          helperText={
            errors.sso_session_hours ||
            'Someone removed from your identity provider keeps Mitigate access for at most this long — deactivate them in Users to cut it off at once.'
          }
          slotProps={{ htmlInput: { min: 1, max: 168 } }}
          sx={{ maxWidth: 420 }}
        />
      </Paper>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button variant="contained" onClick={handleSave} disabled={!dirty || update.isPending}>
          {update.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button
          variant="text"
          onClick={() => {
            setForm(serverForm);
            setErrors({});
          }}
          disabled={!dirty || update.isPending}
        >
          Discard changes
        </Button>
      </Box>
      </Box>

      {/* Right column: what is on now, the link to share, and removal. */}
      <Box sx={{ position: { lg: 'sticky' }, top: 88, minWidth: 0 }}>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            Current method: {current.title}
          </Typography>
          <Chip size="small" label={ssoOn ? 'On' : 'Off'} color={ssoOn ? 'success' : 'default'} />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {current.sentence}
        </Typography>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Your sign-in link
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Share this link with your people. It opens Mitigate's sign-in page for
          your organisation, with your sign-in method already selected.
        </Typography>
        <TextField
          fullWidth
          size="small"
          label="Sign-in link"
          value={loginLink}
          slotProps={{
            input: {
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Copy">
                    <span>
                      <IconButton size="small" onClick={copyLoginLink} disabled={!loginLink}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </InputAdornment>
              ),
            },
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="Short name (used in the link)"
            value={slugDraft}
            onChange={(e) => {
              setSlugDraft(e.target.value);
              setSlugError(null);
            }}
            disabled={!profile || !canEditSlug || updateProfile.isPending}
            error={!!slugError}
            helperText={
              slugError ||
              'Lowercase letters, numbers and hyphens. Changing it changes the link above, so share the new link with your people.'
            }
            sx={{ flex: 1 }}
          />
          <Button
            variant="outlined"
            onClick={handleSaveSlug}
            disabled={!slugDirty || !canEditSlug || updateProfile.isPending}
            sx={{ mt: '2px' }}
          >
            {updateProfile.isPending ? 'Saving…' : 'Save'}
          </Button>
        </Box>
      </Paper>

      {provider && provider.linked_users > 0 && (
        <Alert severity="info" icon={<CheckCircleOutlineIcon />} sx={{ mb: 2 }}>
          {provider.linked_users} {provider.linked_users === 1 ? 'person has' : 'people have'} signed in
          with single sign-on.
        </Alert>
      )}

      {provider && (
        <Button color="error" onClick={() => setConfirmRemove(true)} disabled={remove.isPending}>
          Remove single sign-on
        </Button>
      )}
      </Box>
      </Box>

      <Dialog open={confirmEnforce} onClose={() => setConfirmEnforce(false)}>
        <DialogTitle>Require single sign-on?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Once saved, nobody in your organisation can sign in with a password —
            including administrators. Make sure single sign-on works for you
            first. FiSci support can switch this off if your identity provider
            has an outage.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmEnforce(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              set('enforce_sso', true);
              setConfirmEnforce(false);
            }}
          >
            Require it
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmRemove} onClose={() => setConfirmRemove(false)}>
        <DialogTitle>Remove single sign-on?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Everyone goes back to signing in with a password. People who only
            ever used single sign-on have no password yet — set one for them in
            Users.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRemove(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleRemove} disabled={remove.isPending}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
